import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../api/client'
import type { Deck, DeckCreate, DeckLearningToggleResponse, DeckListResponse, DeckUpdate, QueueCompositionResponse } from '../types'
import { trackEvent } from '../utils/analytics'

/** Shared with the neighbour prefetch; `abortable` is for the prefetch only (see useProgress). */
export const decksQuery = (
  page = 1,
  pageSize = 20,
  folderId?: string | null,
  search?: string,
  abortable = false,
) => queryOptions({
  queryKey: ['decks', page, pageSize, folderId ?? null, search ?? ''],
  queryFn: async (ctx) => {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
    })
    if (folderId) params.set('folder_id', folderId)
    if (search) params.set('search', search)
    const { data } = await api.get<DeckListResponse>(`/api/v1/decks/?${params}`, abortable ? { signal: ctx.signal } : undefined)
    return data
  },
  // 30 s, as Home's queries: arriving within it after the neighbour prefetch
  // does not fetch the list again. Every local change invalidates ['decks'];
  // a change made on another device shows up to 30 s late.
  staleTime: 30_000,
})

export function useDecks(
  page = 1,
  pageSize = 20,
  folderId?: string | null,
  search?: string,
) {
  return useQuery(decksQuery(page, pageSize, folderId, search))
}

export function useDeck(id: string) {
  return useQuery({
    queryKey: ['deck', id],
    queryFn: async () => {
      const { data } = await api.get<Deck>(`/api/v1/decks/${id}`)
      return data
    },
    enabled: !!id,
  })
}

export function useCreateDeck() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: DeckCreate) => {
      const { data } = await api.post<Deck>('/api/v1/decks/', body)
      return data
    },
    onSuccess: (deck) => {
      qc.invalidateQueries({ queryKey: ['decks'] })
      // Folder rows carry a deck count; Explore lists the learner's own public decks.
      if (deck.folder_id) qc.invalidateQueries({ queryKey: ['folders'] })
      if (deck.is_public) qc.invalidateQueries({ queryKey: ['public'] })
      qc.invalidateQueries({ queryKey: ['queue-composition'] })
      trackEvent('deck_created')
    },
  })
}

export function useUpdateDeck() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...body }: DeckUpdate & { id: string }) => {
      const { data } = await api.patch<Deck>(`/api/v1/decks/${id}`, body)
      return data
    },
    onSuccess: (deck, body) => {
      qc.invalidateQueries({ queryKey: ['decks'] })
      // A move changes two folders' deck counts; publishing or unpublishing
      // (or editing a public deck) changes what Explore lists.
      if ('folder_id' in body) qc.invalidateQueries({ queryKey: ['folders'] })
      if (deck.is_public || 'is_public' in body) qc.invalidateQueries({ queryKey: ['public'] })
      // A language change unlinks the deck's cards (ADR-070 Amendment 2):
      // their cached links and sense lists are stale.
      if ('language' in body) {
        qc.invalidateQueries({ queryKey: ['cards', deck.id] })
        qc.invalidateQueries({ queryKey: ['card-senses'] })
      }
      qc.setQueryData(['deck', deck.id], deck)
    },
  })
}

export function useDeleteDeck() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/decks/${id}`)
      return id
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['decks'] })
      // Which folder it was in, or whether it was public, is not known here.
      qc.invalidateQueries({ queryKey: ['folders'] })
      qc.invalidateQueries({ queryKey: ['public'] })
    },
  })
}

export function useToggleDeckLearning() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (deckId: string) => {
      const { data } = await api.post<DeckLearningToggleResponse>(
        `/api/v1/decks/${deckId}/toggle-learning/`,
      )
      return data
    },
    onMutate: async (deckId) => {
      await qc.cancelQueries({ queryKey: ['decks'] })
      await qc.cancelQueries({ queryKey: ['deck', deckId] })
      await qc.cancelQueries({ queryKey: ['queue-composition'] })
      // Snapshot all relevant caches for rollback
      const snapshot = qc.getQueriesData<DeckListResponse>({ queryKey: ['decks'] })
      const singleSnapshot = qc.getQueryData<Deck>(['deck', deckId])
      const queueSnapshot = qc.getQueryData<QueueCompositionResponse>(['queue-composition'])

      // Optimistically flip the flag in list queries
      qc.setQueriesData<DeckListResponse>({ queryKey: ['decks'] }, (old) => {
        if (!old || !('items' in old)) return old
        return {
          ...old,
          items: old.items.map((d) =>
            d.id === deckId ? { ...d, is_active_for_learning: !d.is_active_for_learning } : d,
          ),
        }
      })
      // Optimistically flip the flag in the single-deck cache
      if (singleSnapshot) {
        qc.setQueryData<Deck>(['deck', deckId], {
          ...singleSnapshot,
          is_active_for_learning: !singleSnapshot.is_active_for_learning,
        })
      }
      // Optimistically flip the flag in the queue-composition cache
      if (queueSnapshot) {
        qc.setQueryData<QueueCompositionResponse>(['queue-composition'], {
          ...queueSnapshot,
          sources: queueSnapshot.sources.map((s) =>
            s.deck_id === deckId ? { ...s, is_active_for_learning: !s.is_active_for_learning } : s,
          ),
        })
      }
      return { snapshot, singleSnapshot, queueSnapshot }
    },
    onError: (_err, deckId, context) => {
      // Rollback optimistic changes
      if (context?.snapshot) {
        for (const [key, value] of context.snapshot) {
          qc.setQueryData(key, value)
        }
      }
      if (context?.singleSnapshot) {
        qc.setQueryData(['deck', deckId], context.singleSnapshot)
      }
      if (context?.queueSnapshot) {
        qc.setQueryData(['queue-composition'], context.queueSnapshot)
      }
    },
    onSettled: (_data, _err, deckId) => {
      qc.invalidateQueries({ queryKey: ['decks'] })
      qc.invalidateQueries({ queryKey: ['deck', deckId] })
      qc.invalidateQueries({ queryKey: ['smart-queue'] })
      qc.invalidateQueries({ queryKey: ['queue-composition'] })
    },
  })
}

export function useQueueComposition() {
  return useQuery({
    queryKey: ['queue-composition'],
    queryFn: async () => {
      const { data } = await api.get<QueueCompositionResponse>('/api/v1/decks/queue-composition/')
      return data
    },
    staleTime: 30_000,
  })
}

/** Returns true only if the user owns the deck and it is not a system deck. */
export function isDeckOwner(deck: Deck | undefined, userId: string | null | undefined): boolean {
  if (!deck || !userId) return false
  return deck.user_id === userId && !deck.is_system
}

export async function exportDeckCsv(deckId: string, deckTitle: string) {
  const { data } = await api.get(`/api/v1/decks/${deckId}/export-csv`, {
    responseType: 'blob',
  })
  const url = URL.createObjectURL(data as Blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${deckTitle.replace(/[^a-z0-9]/gi, '_')}.csv`
  a.click()
  URL.revokeObjectURL(url)
}
